import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { DEMO_QUESTIONS, type DemoQuestion } from '@/data/demo-questions';
import { CitationBlock, NoSourceState } from '@/components/citation/CitationBlock';
import { cn } from '@/lib/cn';

type State =
  | { kind: 'idle' }
  | { kind: 'loading'; question: DemoQuestion }
  | { kind: 'answered'; question: DemoQuestion };

/**
 * QuestionDemo — pre-rendered, the conversion linchpin.
 *
 * Per ADR-006 §5.1:
 * - State machine: idle → loading (~600ms theatrical floor) → reveal cascade.
 * - At least one preset must be the no-source variant.
 * - aria-live="polite" announces progress.
 * - Reduced motion: cascade collapses.
 */
export function QuestionDemo({ initialQuestionId }: { initialQuestionId?: string } = {}) {
  // Pre-render the first answered question by default so the demo arrives "alive"
  // on first paint instead of an empty state. Idle is still reachable via prop=null.
  const initial = initialQuestionId
    ? DEMO_QUESTIONS.find((q) => q.id === initialQuestionId)
    : DEMO_QUESTIONS.find((q) => q.kind === 'answered');

  const [state, setState] = useState<State>(
    initial ? { kind: 'answered', question: initial } : { kind: 'idle' },
  );

  function ask(question: DemoQuestion) {
    setState({ kind: 'loading', question });
    const t = window.setTimeout(() => {
      setState({ kind: 'answered', question });
    }, 600);
    return () => window.clearTimeout(t);
  }

  return (
    <div className="cq grid gap-6 border border-line bg-paper p-6 lg:grid-cols-[minmax(0,18rem)_1fr] lg:gap-10 lg:p-8">
      <div>
        <p className="eyebrow">Probar consultas</p>
        <ul role="listbox" aria-label="Preguntas de ejemplo" className="mt-5 flex flex-col gap-1">
          {DEMO_QUESTIONS.map((q, i) => {
            const isActive = state.kind !== 'idle' && state.question.id === q.id;
            return (
              <li key={q.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onClick={() => ask(q)}
                  className={cn(
                    'focus-ring relative flex w-full items-start gap-3 border-l-2 py-2.5 pl-4 pr-2 text-left text-sm transition-colors',
                    isActive
                      ? 'border-accent bg-paper-tinted/60 text-ink'
                      : 'border-transparent text-ink-muted hover:border-line_strong hover:bg-paper-tinted/30 hover:text-ink',
                  )}
                >
                  <span
                    className={cn(
                      'mt-0.5 shrink-0 font-mono text-[0.65rem] tracking-wide',
                      isActive ? 'text-accent' : 'text-ink-subtle',
                    )}
                    aria-hidden="true"
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="leading-snug">{q.prompt}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div aria-live="polite" aria-atomic="false" className="min-h-[14rem]">
        {state.kind === 'idle' && <DemoEmpty />}
        {state.kind === 'loading' && <DemoLoading prompt={state.question.prompt} />}
        {state.kind === 'answered' && <DemoAnswered question={state.question} />}
      </div>
    </div>
  );
}

function DemoEmpty() {
  return (
    <div className="flex h-full flex-col justify-center rounded-md border border-dashed border-line bg-paper-tinted/40 p-6 text-sm text-ink-muted">
      <p className="font-serif italic text-base">
        Las respuestas de SICA siempre incluyen documento, página y jurisdicción.
      </p>
      <p className="mt-3">Elegí una pregunta de la lista para verlo.</p>
    </div>
  );
}

function DemoLoading({ prompt }: { prompt: string }) {
  return (
    <div className="border border-line bg-paper">
      <div className="px-5 py-4">
        <p className="eyebrow">Consulta</p>
        <p className="mt-2 font-serif text-lg leading-snug text-ink">{prompt}</p>
      </div>
      <div className="border-t border-line px-5 py-5">
        <p className="eyebrow">Respuesta SICA</p>
        <div className="mt-3 flex items-center gap-2 text-sm text-ink-muted">
          <Loader2 size={16} className="animate-spin" />
          <span>Buscando fuente…</span>
        </div>
      </div>
    </div>
  );
}

function DemoAnswered({ question }: { question: DemoQuestion }) {
  return (
    <div className="border border-line bg-paper">
      <div className="px-5 py-4">
        <p className="eyebrow">Consulta</p>
        <p className="mt-2 font-serif text-lg leading-snug text-ink">{question.prompt}</p>
      </div>
      <div className="border-t border-line px-5 py-5">
        <p className="eyebrow">Respuesta SICA</p>
        {question.kind === 'answered' && question.answer && question.citation ? (
          <>
            <p className="mt-3 animate-fade-in text-sm leading-relaxed text-ink">{question.answer}</p>
            <div className="mt-4 animate-fade-in-delayed opacity-0">
              <CitationBlock citation={question.citation} />
            </div>
          </>
        ) : (
          <div className="mt-3 animate-fade-in">
            <NoSourceState prompt={question.prompt} />
          </div>
        )}
      </div>
    </div>
  );
}
